import { useEffect, useState } from 'react'
import type {
  NewsletterPreviewResponse,
  NewsletterSendAllResponse,
} from '@natalan/capehost-contracts'
import { useToast } from '@/hooks/use-toast'
import { api } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { AlertTriangle, RefreshCw, Send } from 'lucide-react'

// Normalizes whatever a catch clause hands us (typed `unknown`) into a real
// Error so it can be stored/rendered the same way api/client.ts's own
// AuthorizationError/ApiUnreachableError subclasses are (ErrorState
// branches on `instanceof` for those) -- every actual throw site in
// api/client.ts already throws a real Error, so the fallback branch here is
// just a type-safety net, not a behavior change.
function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error))
}

export function NewslettersPage() {
  const { toast } = useToast()
  // Which issue is "current" comes from the backend's registry (one file per
  // month there), not a hardcoded slug here — so nothing in admin-ui needs to
  // change when a new month's issue is added.
  const [slug, setSlug] = useState<string | null>(null)
  const [preview, setPreview] = useState<NewsletterPreviewResponse | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(true)
  const [previewError, setPreviewError] = useState<Error | null>(null)

  const [testEmail, setTestEmail] = useState('')
  const [isSendingTest, setIsSendingTest] = useState(false)
  // Absolute expiry from the backend's send-test response — not a client-guessed
  // window, so this can never drift from the server's actual KV guard TTL.
  const [testGateExpiresAt, setTestGateExpiresAt] = useState<string | null>(null)

  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isSendingAll, setIsSendingAll] = useState(false)
  const [sendAllResult, setSendAllResult] = useState<NewsletterSendAllResponse | null>(null)

  const loadPreview = async () => {
    setIsLoadingPreview(true)
    setPreviewError(null)
    try {
      const { latest_slug: latestSlug } = await api.listNewsletters()
      if (!latestSlug) {
        throw new Error('No newsletter issues are registered yet.')
      }
      setSlug(latestSlug)
      const data = await api.getNewsletterPreview(latestSlug)
      setPreview(data)
    } catch (error: unknown) {
      setPreviewError(toError(error))
    } finally {
      setIsLoadingPreview(false)
    }
  }

  useEffect(() => {
    const run = async () => {
      await loadPreview()
    }
    run()
  }, [])

  // Nothing else re-renders this component when the gate's expiry passes, so
  // without this the button would stay visually enabled past expiry until the
  // admin interacts with the page again (they'd just get a 409 on click).
  const [, forceRerender] = useState(0)
  useEffect(() => {
    if (!testGateExpiresAt) return undefined
    const msRemaining = new Date(testGateExpiresAt).getTime() - Date.now()
    if (msRemaining <= 0) return undefined
    const timer = setTimeout(() => forceRerender(tick => tick + 1), msRemaining + 100)
    return () => clearTimeout(timer)
  }, [testGateExpiresAt])

  // forceRerender above already re-triggers this render at the moment the
  // gate expires, so the Date.now() reference here stays accurate at the
  // times that matter despite not being tracked as a dependency.
  const testGateActive =
    testGateExpiresAt !== null &&
    // eslint-disable-next-line react-hooks/purity
    Date.now() < new Date(testGateExpiresAt).getTime()

  const handleSendTest = async () => {
    if (!testEmail.trim() || !slug) return
    setIsSendingTest(true)
    try {
      const result = await api.sendNewsletterTest(slug, testEmail.trim())
      if (result.success && result.expires_at) {
        setTestGateExpiresAt(result.expires_at)
        toast({ title: 'Test sent', description: `Newsletter sent to ${testEmail.trim()}.` })
      } else if (result.success) {
        // Send succeeded but the server-side gate guard failed to persist (logged
        // there) — do not unlock send-to-all on an assumption the guard exists.
        setTestGateExpiresAt(null)
        toast({
          title: 'Test sent, but send-all is still locked',
          description: 'The test email sent, but the server could not record it. Try again.',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Test send failed',
          description: result.error || 'Unknown error',
          variant: 'destructive',
        })
      }
    } catch (error: unknown) {
      toast({
        title: 'Test send failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setIsSendingTest(false)
    }
  }

  const handleConfirmSendAll = async () => {
    if (!slug) return
    setIsSendingAll(true)
    try {
      const result = await api.sendNewsletterToAllOwners(slug)
      setSendAllResult(result)
      toast({
        title: 'Newsletter sent',
        description: `${result.sent} sent, ${result.skipped} already sent, ${result.failed} failed (of ${result.recipient_count} owners).`,
      })
      setIsConfirmOpen(false)
    } catch (error: unknown) {
      toast({
        title: 'Send to all owners failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      })
      // Only re-lock the button when the backend actually rejected for a lapsed
      // test window (409 test_required) — an unrelated failure (network blip, a
      // 500 before the send loop even starts) shouldn't clear a still-valid gate.
      if (error instanceof Error && error.message.includes('test_required')) {
        setTestGateExpiresAt(null)
      }
    } finally {
      setIsSendingAll(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Preview: {slug ?? '...'}</CardTitle>
          <CardDescription>
            Current content for the monthly owner newsletter.
            {preview?.recipient_count != null && (
              <>
                {' '}
                Would go to <span className="font-medium text-text">
                  {preview.recipient_count}
                </span>{' '}
                owner{preview.recipient_count === 1 ? '' : 's'}.
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingPreview ? (
            <LoadingSkeleton className="h-96 w-full" />
          ) : previewError ? (
            <ErrorState error={previewError} />
          ) : (
            <div className="rounded border bg-white">
              <iframe
                srcDoc={preview?.html}
                title="Newsletter preview"
                className="h-[60vh] w-full border-0"
                sandbox="allow-same-origin"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Send a test</CardTitle>
          <CardDescription>
            Send one copy to any address to check formatting before sending to owners. Required
            before &quot;Send to all owners&quot; unlocks below (expires after an hour).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Input
              type="email"
              placeholder="you@capehost.ai"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              className="sm:max-w-xs"
            />
            <Button
              onClick={handleSendTest}
              disabled={isSendingTest || !testEmail.trim() || !slug}
              variant="secondary"
            >
              {isSendingTest ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send test
                </>
              )}
            </Button>
          </div>
          {testGateActive && (
            <p className="mt-2 text-sm text-textMuted">
              Test sent. &quot;Send to all owners&quot; is unlocked for the next hour.
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-danger/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-danger">
            <AlertTriangle className="h-5 w-5" />
            Send to all owners
          </CardTitle>
          <CardDescription>
            Sends the newsletter above to every active or invited owner. This cannot be undone.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {sendAllResult && (
            <p className="text-sm text-textMuted">
              Last send: {sendAllResult.sent} sent, {sendAllResult.skipped} skipped,{' '}
              {sendAllResult.failed} failed (of {sendAllResult.recipient_count} owners).
            </p>
          )}
          <Button
            onClick={() => setIsConfirmOpen(true)}
            disabled={!testGateActive}
            variant="destructive"
            title={testGateActive ? undefined : 'Send a test first'}
          >
            {testGateActive ? 'Send to all owners' : 'Send a test first'}
          </Button>
        </CardContent>
      </Card>

      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-danger">
              <AlertTriangle className="h-5 w-5" />
              Confirm send to all owners
            </DialogTitle>
            <DialogDescription>
              This will immediately email the {slug} newsletter to{' '}
              {preview?.recipient_count ?? 'all'} owner
              {preview?.recipient_count === 1 ? '' : 's'}. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsConfirmOpen(false)}
              disabled={isSendingAll}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmSendAll}
              disabled={isSendingAll || !testGateActive}
            >
              {isSendingAll ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send to all owners
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
